import { useMemo } from "react";

import {
  AnalyzeTinybirdResourceResponseBodyType,
  ExperienceFe,
  GetExperienceResponseBodyType,
} from "@intelligems/ig-types";
import { GraphConfig } from "@intelligems/ig-types";
import { getDaysFromHistory } from "@intelligems/ig-analytics";
import { ensureNumber } from "@intelligems/ig-utils";
import { DashboardPageName } from "@src/api/types/dashboards";
import { getExperienceTotal } from "@src/features/analytics/utils";
import { shouldShowMobileExperience } from "@src/utils/deviceDetection";
import { UseQueryResult } from "@tanstack/react-query";

import { ShareDesktopView } from "./components/ShareDesktopView";
import {
  ShareAnalyticsContextValue,
  ShareAnalyticsProvider,
  ShareTab,
} from "./context/ShareAnalyticsContext";
import { ShareMobileView } from "./mobile/ShareMobileView";

interface ShareBodyProps {
  experience: GetExperienceResponseBodyType;
  experienceFe: ExperienceFe;
  analyticsData: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  graphConfigs: GraphConfig[];
  pageName: DashboardPageName;
  activeTab: ShareTab;
  availablePages: DashboardPageName[];
}

export const ShareBody = ({
  experience,
  experienceFe,
  analyticsData,
  graphConfigs,
  pageName,
  activeTab,
  availablePages,
}: ShareBodyProps) => {
  const showMobile = shouldShowMobileExperience();

  // Calculate experience totals
  const experienceTotals = useMemo(
    () => ({
      visitors: getExperienceTotal(analyticsData, "n_visitors"),
      orders: getExperienceTotal(analyticsData, "n_orders"),
      revenue: getExperienceTotal(analyticsData, "net_revenue"),
    }),
    [analyticsData]
  );

  // Calculate test duration
  const testDuration = useMemo(
    () =>
      getDaysFromHistory(
        experience.experience.experienceActions || [],
        ensureNumber(experience.experience.pausedAtTs) ?? Date.now()
      ),
    [experience.experience]
  );

  // Extract currency from experience data
  const experienceCurrency = experience.experience.currency;
  const currencyCode = experienceCurrency?.code || "USD";
  const currencyLocale = experienceCurrency?.country
    ? `${experienceCurrency.country}-${currencyCode}`
    : "en-US";
  const currencyConfig = {
    code: currencyCode,
    locale: currencyLocale,
  };

  // Get latest run timestamp
  const latestRunTs = analyticsData[0]?.data?.latestRunTs;

  const timezone = "UTC";

  // Only show Configure Analytics tab for experiments (not personalizations)
  const showConfigureAnalytics =
    experience.experience.category === "experiment";

  const contextValue: ShareAnalyticsContextValue = {
    experience: experience.experience,
    experienceFe,
    variations: experience.experience.variations || [],
    analyticsData,
    currencyConfig,
    dashboardFilters: {
      hiddenStartTime: experience.experience.startedAtTs || 0,
      hiddenEndTime: experience.experience.pausedAtTs || Date.now(),
      compareVariationId: experience.experience.variations?.[0]?.id || "",
    },
    graphConfigs,
    isLoading: analyticsData.some((q) => q.isLoading || q.isPending),
    experienceTotals,
    testDuration,
    latestRunTs,
    timezone,
    pageName,
    organization: { timezone },
    availablePages,
    activeTab,
    showConfigureAnalytics,
  };

  return (
    <ShareAnalyticsProvider value={contextValue}>
      {showMobile ? <ShareMobileView /> : <ShareDesktopView />}
    </ShareAnalyticsProvider>
  );
};
